// TODO: Fix eslint issues the next time this file is edited.
/* eslint-disable @next/next/no-img-element */
import React from 'react';
import * as Accordion from '@volvo-cars/react-accordion';
import * as Breadcrumbs from '@volvo-cars/react-breadcrumbs';
import * as Descendants from '@volvo-cars/react-descendants';
import * as Faqs from '@volvo-cars/react-faqs';
import {
  InfoBanner,
  InfoBannerWithCTA,
  InfoLink,
  useDismiss,
} from '@volvo-cars/react-info-banner';
import { ContentBlock, TitledOverlay } from '@volvo-cars/react-overlay';
import * as Tracking from '@volvo-cars/tracking';
import copyToClipboard from 'copy-to-clipboard';
import { renderToStaticMarkup as ReactDomRenderToStaticMarkup } from 'react-dom/server';
import * as VCCUI from 'vcc-ui';
import { Typography } from 'vcc-ui-design-util-components';
import { TYPES as icons } from 'vcc-ui-tokens-to-migrate';
import DataLayerViewer from './DataLayerViewer';
import FelaWrapper from './fela-wrapper';
import MDXCodeBlock from './mdx-code-block';

const {
  Theme,
  useTheme,
  Block,
  Flex,
  Inline,
  Spacer,
  Text,
  TabNavItem,
  TabNav,
  View,
  Icon,
  Click,
} = VCCUI;

/**
 * Group by a key in the array of objects
 * @param arr : array
 * @param key : string
 * @returns array
 */
const groupBy = (arr, key) => {
  return arr.reduce((r, a) => {
    r[a[key]] = r[a[key]] || [];
    r[a[key]].push(a);
    return r;
  }, Object.create(null));
};

const Icons = () => {
  const theme = useTheme();

  const iconsArr = icons.map((icon) => {
    const iconNameParts = icon.split('-');

    if (iconNameParts.length === 3) {
      return {
        category: iconNameParts[0],
        name: iconNameParts[1],
        size: iconNameParts[2],
      };
    } else {
      return {
        category: 'core',
        name: iconNameParts[0],
        size: iconNameParts[1],
      };
    }
  });

  const reducedIcons = groupBy(iconsArr, 'category');

  const reducedIconsName = Object.entries(reducedIcons).reduce(
    (acc, [key, value]) => {
      const icons = groupBy(value, 'name');
      acc.push({ key, icons });
      return acc;
    },
    []
  );

  return (
    <>
      <Flex
        extend={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}
      >
        {reducedIconsName.map((redIcons, index) => {
          return (
            <View key={index} width="100%" display="block">
              <H3 id={redIcons.key}>{redIcons.key}</H3>
              <hr />
              {Object.entries(redIcons.icons).map(([key, value]) => {
                return (
                  <View
                    as="a"
                    href={'#' + key}
                    id={key}
                    key={key}
                    display="block"
                    marginBottom={1}
                    extend={{
                      textDecoration: 'none',
                      border: `1px solid ${theme.color.ornament.border}`,
                      position: 'relative',
                      '& [role=img]': { display: 'none' },
                      '&:hover [role=img]': { display: 'inline' },
                    }}
                  >
                    <View padding={1} direction="row">
                      <Text
                        as="p"
                        subStyle="emphasis"
                        variant="columbus"
                        id={key}
                        extend={{
                          display: 'inline',
                          borderBottom: `1px solid ${theme.color.ornament.border}`,
                        }}
                      >
                        {key}
                      </Text>
                      <InlineLinkImage />
                    </View>
                    {value.map((icon, index) => {
                      let iconName =
                        icon.category === 'core'
                          ? `${icon.name}-${icon.size}`
                          : `${icon.category}-${icon.name}-${icon.size}`;

                      return (
                        <Click
                          key={index}
                          id={iconName}
                          onClick={() =>
                            copyToClipboard(`<Icon type="${iconName}" />`)
                          }
                          extend={{
                            display: 'inline-block',
                            margin: theme.baselineGrid,
                            padding: theme.baselineGrid,
                            textAlign: 'center',
                            color: theme.color.foreground.primary,
                          }}
                        >
                          <Icon type={iconName} />
                        </Click>
                      );
                    })}
                  </View>
                );
              })}
            </View>
          );
        })}
      </Flex>
    </>
  );
};

const InlineLinkImage = () => {
  const theme = useTheme();
  return (
    <Inline
      role="img"
      aria-label="link"
      extend={{
        color: theme.color.foreground.primary,
        verticalAlign: 'middle',
        paddingLeft: 16,
        fontSize: 14,
      }}
    >
      &#x1f517;
    </Inline>
  );
};

const Anchor = ({ id, children }) => {
  const theme = useTheme();
  return (
    <Inline
      as="a"
      id={id}
      href={'#' + id}
      extend={{
        '& img': {
          opacity: 0,
          transition: 'opacity 100ms ease-in-out',
          marginLeft: 8,
        },
        '&:hover img': { opacity: 1 },
        position: 'relative',
        color: theme.color.foreground.primary,
        textDecoration: 'none',
      }}
    >
      {children}
      <Icon type="link-24" />
    </Inline>
  );
};

const H1 = ({ id, children }) => (
  <>
    <Spacer size={4} />
    <Text variant="peary">
      <Anchor id={id}>{children}</Anchor>
    </Text>
    <Spacer size={4} />
  </>
);

const H2 = ({ id, children }) => (
  <>
    <Spacer size={2} />
    <Text variant="cook" as="h2">
      <Anchor id={id}>{children}</Anchor>
    </Text>
    <Spacer size={4} />
  </>
);

const H3 = ({ id, children }) => (
  <>
    <Spacer size={2} />
    <Text variant="ootah" as="h3">
      <Anchor id={id}>{children}</Anchor>
    </Text>
    <Spacer size={2} />
  </>
);

const H4 = (props) => (
  <>
    <Spacer size={2} />
    <Text variant="hillary" as="h4" {...props} />
    <Spacer size={2} />
  </>
);

const A = (props) => <Text subStyle="inline-link" {...props} />;

export const Table = ({ children, ...props }) => (
  <>
    <Block
      as="table"
      extend={{
        minWidth: '100%',
        padding: 8,
        display: 'table',
        borderCollapse: 'collapse',
      }}
      {...props}
    >
      {children}
    </Block>
    <Spacer size={4} />
  </>
);

export const Td = ({ children, ...props }) => (
  <Text
    as="td"
    variant="bates"
    extend={{
      display: 'table-cell',
      padding: '8px 16px 8px 8px',
      verticalAlign: 'top',
    }}
    {...props}
  >
    {children}
  </Text>
);

export const Tr = (props) => {
  const theme = useTheme();
  return (
    <Block
      as="tr"
      extend={{
        display: 'table-row',
        borderBottom: `1px solid ${theme.color.ornament.divider}`,
      }}
      {...props}
    />
  );
};

export const Th = ({ children, ...props }) => {
  const theme = useTheme();
  return (
    <Text
      as="th"
      subStyle="emphasis"
      extend={{
        display: 'table-cell',
        borderBottom: `1px solid ${theme.color.ornament.border}`,
        padding: 8,
        whiteSpace: 'nowrap',
        textAlign: 'left',
      }}
      {...props}
    >
      <Flex extend={{ flexDirection: 'row' }}>{children}</Flex>
    </Text>
  );
};

const P = (props) => {
  const theme = useTheme();
  return (
    <>
      <Text extend={{ color: theme.color.foreground.secondary }} {...props} />
      <Spacer size={2} />
    </>
  );
};

const Li = ({ children }) => {
  const theme = useTheme();
  return (
    <>
      <Text
        as="li"
        extend={{
          display: 'list-item',
          color: theme.color.foreground.secondary,
        }}
      >
        {children}
      </Text>
    </>
  );
};
export const Badgen = ({ name }) => {
  return (
    <>
      <Flex extend={{ display: 'flex', flexDirection: 'row' }}>
        <img src={`https://badgen.net/npm/v/${name}`} alt={name} />
        <Spacer />
        <img
          src={`https://badgen.net/bundlephobia/minzip/${name}`}
          alt={name}
        />
        <Spacer />
        <img
          src={`https://badgen.net/bundlephobia/tree-shaking/${name}`}
          alt={name}
        />
      </Flex>
      <Spacer size={4} />
    </>
  );
};
export const Install = ({ name }) => {
  const [active, setActive] = React.useState(1);
  return (
    <View>
      <TabNav enableLineTransition textAlign="left" duration={50}>
        <TabNavItem
          isActive={active === 1}
          onClick={() => {
            setActive(1);
          }}
        >
          yarn
        </TabNavItem>
        <TabNavItem
          isActive={active === 2}
          onClick={() => {
            setActive(2);
          }}
        >
          npm
        </TabNavItem>
      </TabNav>
      <MDXCodeBlock language="bash">
        {active === 1 ? `$ yarn add ${name}` : `$ npm install ${name}`}
      </MDXCodeBlock>
    </View>
  );
};
const Ul = (props) => (
  <>
    <Flex as="ul" {...props} extend={{ margin: 0 }} />
    <Spacer size={2} />
  </>
);

const Strong = (props) => <Text subStyle="emphasis" {...props} />;

const Code = (props) => (
  <>
    <MDXCodeBlock {...props} />
    <Spacer size={2} />
  </>
);

const InlineCode = (props) => {
  const theme = useTheme();
  return (
    <Inline
      {...props}
      extend={{
        fontFamily: 'monospace',
        fontSize: 'inherit',
        lineHeight: '1rem',
        padding: '0.4rem',
        background: theme.color.background.secondary,
      }}
      as="code"
    />
  );
};

const Pre = (props) => <Block as="div" {...props} />;

const Blockquote = (props) => {
  const { color } = useTheme();
  return (
    <Block
      extend={{
        paddingLeft: 12,
        margin: '24px 0',
        borderLeft: `2px solid ${color.ornament.border}`,
      }}
      as="div"
      {...props}
    />
  );
};

const Img = ({ ...props }) => {
  return (
    <Block
      as="img"
      {...props}
      extend={{
        maxWidth: '100%',
      }}
    />
  );
};

export const MDXComponents = {
  ...Accordion,
  ...Breadcrumbs,
  ...Descendants,
  ...Faqs,
  ...Tracking,
  ...VCCUI,
  a: A,
  Badgen,
  blockquote: Blockquote,
  code: Code,
  DataLayerViewer,
  FelaWrapper,
  h1: H1,
  h2: H2,
  h3: H3,
  h4: H4,
  Icons,
  InfoBanner,
  InfoBannerWithCTA,
  TitledOverlay,
  ContentBlock,
  InfoLink,
  inlineCode: InlineCode,
  Install,
  li: Li,
  ol: Ul,
  p: P,
  pre: Pre,
  ReactDomRenderToStaticMarkup,
  strong: Strong,
  table: Table,
  td: Td,
  th: Th,
  tr: Tr,
  Typography,
  ul: Ul,
  useDismiss,
};

export const blogComponents = (post) => ({
  h1: function h1({ ...props }) {
    return (
      <Theme>
        {(theme) => (
          <>
            <Spacer size={4} />
            <Text variant="peary" {...props} />
            <Spacer size={2} />
            <Text
              as="time"
              extend={{ color: theme.color.foreground.secondary }}
              variant="bates"
              dateTime={post.date}
            >
              Published {post.date}
            </Text>
            <Spacer size={4} />
          </>
        )}
      </Theme>
    );
  },
  img: Img,
  blockquote: function blockquote(props) {
    return (
      <Text
        as="blockquote"
        extend={{
          background: '#f5f5f5',
          margin: '0',
          padding: '24px',
          position: 'relative',
          ':after': {
            top: '100%',
            right: '100px',
            border: 'solid transparent',
            content: '" "',
            height: 0,
            width: 0,
            position: 'absolute',
            pointerEvents: 'none',
            borderColor: 'rgba(0, 0, 0, 0)',
          },
          '& span:after': {
            content: '"”"',
          },
          '& span:before': {
            content: '"“"',
          },
        }}
        {...props}
      />
    );
  },
});
